import React, { useEffect, useRef, useState } from "react";
import { useCombatLog } from "../hooks/useCombatLog.js";
import { useLayout, type LayoutMode } from "../hooks/useLayout.js";
import { highlightFeedLine } from "./feedHighlight.js";

interface CombatLogProps {
  panel?: boolean;
}

/** Floating-overlay timing: each message holds for HOLD_MS then fades over FADE_MS. */
const HOLD_MS = 4000;
const FADE_MS = 2000;
const TOTAL_MS = HOLD_MS + FADE_MS;
/**
 * Busy-combat compression: past this many live entries the overlay
 * switches to the fast clock so a hot fight doesn't wallpaper a phone
 * screen — and it hard-caps the rendered lines regardless.
 */
const BUSY_THRESHOLD = 5;
const HOLD_FAST_MS = 1500;
const FADE_FAST_MS = 800;
const MAX_LIVE: Record<LayoutMode, number> = {
  "mobile-portrait": 5,
  "mobile-landscape": 6,
  desktop: 10,
};

export function CombatLog({ panel = false }: CombatLogProps) {
  const log = useCombatLog();
  const layout = useLayout();
  const containerRef = useRef<HTMLDivElement>(null);

  // Drive fade re-renders in floating mode. Panel mode doesn't fade.
  const [, setTick] = useState(0);
  useEffect(() => {
    if (panel) return;
    const id = window.setInterval(() => setTick((t) => t + 1), 250);
    return () => window.clearInterval(id);
  }, [panel]);

  // Keep newest visible (panel mode only — floating overlay isn't scrollable).
  useEffect(() => {
    if (!panel) return;
    if (containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  }, [log, panel]);

  if (panel) {
    if (log.length === 0) return null;
    return (
      <div ref={containerRef} style={panelStyle}>
        {log.map((e, i) => (
          <div
            key={e.id}
            style={{ color: i === log.length - 1 ? "#fff" : "#aaa" }}
          >
            {highlightFeedLine(e.message)}
          </div>
        ))}
      </div>
    );
  }

  // Floating overlay: render only entries still inside the hold+fade
  // window. When combat runs hot the whole overlay switches to the fast
  // clock and hard-caps the visible lines, so a burst of feed traffic
  // can't wallpaper a phone screen.
  const now = Date.now();
  const withinSlow = log.filter((e) => now - e.pushedAt < TOTAL_MS);
  const busy = withinSlow.length > BUSY_THRESHOLD;
  const hold = busy ? HOLD_FAST_MS : HOLD_MS;
  const fade = busy ? FADE_FAST_MS : FADE_MS;
  const live = withinSlow
    .filter((e) => now - e.pushedAt < hold + fade)
    .slice(-MAX_LIVE[layout]);
  if (live.length === 0) return null;

  return (
    <div style={floatingStyle(layout)}>
      {live.map((e) => {
        const age = now - e.pushedAt;
        const opacity = age < hold ? 1 : Math.max(0, 1 - (age - hold) / fade);
        return (
          <div key={e.id} style={{ color: "#fff", opacity }}>
            {highlightFeedLine(e.message)}
          </div>
        );
      })}
    </div>
  );
}

const panelStyle: React.CSSProperties = {
  flex: 1,
  overflowY: "auto",
  background: "transparent",
  color: "#ccc",
  fontSize: 12,
  lineHeight: 1.5,
  padding: "6px 10px",
  borderTop: "1px solid rgba(255,255,255,0.08)",
  pointerEvents: "auto",
  userSelect: "none",
};

function floatingStyle(layout: LayoutMode): React.CSSProperties {
  const base: React.CSSProperties = {
    position: "fixed",
    background: "rgba(0,0,0,0.55)",
    color: "#ccc",
    fontSize: 12,
    lineHeight: 1.4,
    padding: "6px 10px",
    borderRadius: 4,
    pointerEvents: "none",
    userSelect: "none",
  };
  if (layout === "mobile-portrait") {
    // Anchor the feed's *bottom* edge to the FPV/dock seam (top: 56vh).
    // We can't use `bottom: 44vh` directly — iOS Safari measures `bottom`
    // from the large-viewport bottom (below the browser chrome), which
    // floats the feed up into the middle of the FPV when the URL bar is
    // visible. Pinning `top` and translating by 100% uses the same
    // coordinate system as NavCluster (`top: calc(56vh + 8px)`), so the
    // feed always sits 4px above the seam.
    return {
      ...base,
      left: 8,
      right: 8,
      top: "calc(50dvh - 4px)",
      transform: "translateY(-100%)",
    };
  }
  if (layout === "mobile-landscape") {
    // Above the bottom-anchored nav cluster (56-tall buttons + 12px gap +
    // safe-area). 80px clears the cluster on every common landscape device.
    return {
      ...base,
      left: 8,
      right: 8,
      bottom: "calc(80px + env(safe-area-inset-bottom, 0px))",
    };
  }
  // Desktop fallback (rarely used — DesktopHUD passes panel={true}).
  return { ...base, left: 8, bottom: 8, width: 340, maxHeight: 160 };
}
